import type { EmbedConfig, EmbedProductConfig } from './urlGenerator';

export interface ImportParseResult {
  config: Partial<EmbedConfig>;
  width?: string;
  height?: string;
  mobileAutoResize: boolean;
  warnings: string[];
  error?: string;
  extractedPartnerKey?: string;
}

function parseCommaSeparated(value: string | null): string[] {
  if (!value || value.trim() === '') return [];
  return value
    .split(',')
    .map(s => s.trim())
    .filter(s => s.length > 0);
}

export function extractSrcUrl(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed.startsWith('<')) {
    const match = trimmed.match(/src="([^"]+)"/);
    if (!match) return null;
    // Browsers HTML-encode & as &amp; inside attribute values, so decode before parsing.
    const decoded = match[1].replace(/&amp;/g, '&');
    try {
      new URL(decoded);
      return decoded;
    } catch {
      return null;
    }
  }
  try {
    new URL(trimmed);
    return trimmed;
  } catch {
    return null;
  }
}

// Width is in `style="...max-width:Xpx..."` — the iframe width attribute is always "100%".
// Height is in the iframe `height="X"` attribute as a plain number.
export function extractIframeDimensions(input: string): {
  width?: string;
  height?: string;
} {
  const widthMatch = input.match(/max-width:(\d+)px/);
  const heightMatch = input.match(/\bheight="(\d+)"/);
  return {
    width: widthMatch?.[1],
    height: heightMatch?.[1]
  };
}

// Returns true when auto-resize was active in the snippet, or when we cannot tell
// (default to enabled per PRD §4.7 req 20).
export function inferMobileAutoResize(input: string, url: string): boolean {
  if (input.includes('namebubbles:embed-resize')) return true;
  try {
    if (new URLSearchParams(new URL(url).search).get('auto_resize') === '1')
      return true;
  } catch {
    // ignore malformed URL
  }
  return true;
}

// Returns null when the blob cannot be decoded (malformed base64 or JSON).
export function decodeAppearanceBlob(ap: string): Partial<EmbedConfig> | null {
  try {
    const decoded = JSON.parse(atob(ap)) as Record<string, string>;
    const result: Partial<EmbedConfig> = {};
    if (decoded.ff) result.fontFamily = decoded.ff;
    if (decoded.fc) result.customFontClass = decoded.fc;
    if (decoded.br) result.brandBannerColor = decoded.br;
    if (decoded.ac) result.accentColor = decoded.ac;
    if (decoded.pb) result.primaryButtonColor = decoded.pb;
    if (decoded.pt) result.primaryButtonText = decoded.pt;
    if (decoded.sb) result.secondaryButtonColor = decoded.sb;
    if (decoded.st) result.secondaryButtonText = decoded.st;
    if (decoded.hb) result.headerBgColor = decoded.hb;
    if (decoded.bb) result.bodyBgColor = decoded.bb;
    if (decoded.bd) result.borderColor = decoded.bd;
    if (decoded.tc) result.textColor = decoded.tc;
    if (decoded.ts) result.secondaryTextColor = decoded.ts;
    if (decoded.bn) result.brandName = decoded.bn;
    return result;
  } catch {
    return null;
  }
}

export function decodeProductsBlob(
  products: string
): EmbedProductConfig[] | null {
  try {
    const decoded = JSON.parse(atob(products));
    if (!Array.isArray(decoded)) return null;
    return decoded as EmbedProductConfig[];
  } catch {
    return null;
  }
}

export function parseImportedSnippet(input: string): ImportParseResult {
  const trimmed = input.trim();

  if (!trimmed) {
    return {
      config: {},
      mobileAutoResize: true,
      warnings: [],
      error: 'Please paste an iframe snippet or a URL.'
    };
  }

  const isIframe = trimmed.startsWith('<');
  const srcUrl = extractSrcUrl(trimmed);

  if (!srcUrl) {
    return {
      config: {},
      mobileAutoResize: true,
      warnings: [],
      error:
        'Could not find a valid URL. Please paste a full iframe snippet (e.g. <iframe src="...">) or a direct URL.'
    };
  }

  let params: URLSearchParams;
  try {
    params = new URLSearchParams(new URL(srcUrl).search);
  } catch {
    return {
      config: {},
      mobileAutoResize: true,
      warnings: [],
      error:
        'Could not find a valid URL. Please paste a full iframe snippet (e.g. <iframe src="...">) or a direct URL.'
    };
  }

  const productTag = params.get('product_tag');
  if (!productTag) {
    return {
      config: {},
      mobileAutoResize: true,
      warnings: [],
      error: 'The URL is missing a required product_tag parameter.'
    };
  }

  const warnings: string[] = [];
  const config: Partial<EmbedConfig> = { productTag };

  const partnerKey = params.get('partner_key');
  if (partnerKey) config.partnerKey = partnerKey;

  const partnerId = params.get('partner_id');
  if (partnerId) config.partnerId = partnerId;

  const affiliateRfsn = params.get('rfsn');
  if (affiliateRfsn) config.affiliateRfsn = affiliateRfsn;

  const shopifyVariantId = params.get('shopify_variant_id');
  if (shopifyVariantId) config.shopifyVariantId = shopifyVariantId;

  const shopifyDomain = params.get('shopify_domain');
  if (shopifyDomain) config.shopifyDomain = shopifyDomain;

  const productTitle = params.get('product_title');
  if (productTitle) config.productTitle = productTitle;

  const priceMin = params.get('price_min');
  if (priceMin) config.productPriceMin = priceMin;

  const priceMax = params.get('price_max');
  if (priceMax) config.productPriceMax = priceMax;

  const currency = params.get('currency');
  if (currency) config.productCurrency = currency;

  const compareAtPriceMin = params.get('compare_at_price_min');
  if (compareAtPriceMin) config.productCompareAtPriceMin = compareAtPriceMin;

  const styles = parseCommaSeparated(params.get('styles'));
  if (styles.length > 0) config.allowedStyles = styles;

  const colors = parseCommaSeparated(params.get('colors'));
  if (colors.length > 0) config.allowedColors = colors;

  const fonts = parseCommaSeparated(params.get('fonts'));
  if (fonts.length > 0) config.allowedFonts = fonts;

  const icons = parseCommaSeparated(params.get('icons'));
  if (icons.length > 0) config.allowedIcons = icons;

  const materials = parseCommaSeparated(params.get('materials'));
  if (materials.length > 0) config.allowedMaterials = materials;

  const apParam = params.get('ap');
  if (apParam) {
    const appearance = decodeAppearanceBlob(apParam);
    if (appearance === null) {
      warnings.push(
        'The appearance (ap) parameter could not be decoded. Appearance settings will be skipped.'
      );
    } else {
      Object.assign(config, appearance);
    }
  }

  const productsParam = params.get('products');
  if (productsParam) {
    const products = decodeProductsBlob(productsParam);
    if (products === null) {
      warnings.push(
        'The multi-product (products) parameter could not be decoded. Product settings will be skipped.'
      );
    } else {
      config.products = products;
    }
  }

  const { width, height } = isIframe
    ? extractIframeDimensions(trimmed)
    : { width: undefined, height: undefined };
  const mobileAutoResize = inferMobileAutoResize(trimmed, srcUrl);

  return {
    config,
    width,
    height,
    mobileAutoResize,
    warnings,
    extractedPartnerKey: partnerKey ?? undefined
  };
}
